import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Grid, Map, Calendar, RefreshCw } from "lucide-react";
import { eventService, EventWithAttendees, EventInsert } from "@/services/eventService";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import EventCard from "@/components/events/EventCard";
import CreateEventDialog from "@/components/events/CreateEventDialog";
import EventDetailsDialog from "@/components/events/EventDetailsDialog";
import AdPanel from "@/components/ads/AdPanel";
import RightSideMenu from "@/components/navigation/RightSideMenu";

const Events = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventWithAttendees | null>(null);
  const [activeTab, setActiveTab] = useState("grid");
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: events = [], isLoading, error, refetch } = useQuery({
    queryKey: ["events"],
    queryFn: eventService.getEvents,
  });

  console.log("Events query result:", { events, isLoading, error });

  const createEventMutation = useMutation({
    mutationFn: async (eventData: EventInsert) => {
      if (!user?.id) throw new Error("User not authenticated");
      return eventService.createEvent({
        ...eventData,
        created_by: user.id
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setIsCreateDialogOpen(false);
      toast({
        title: "Event Created!",
        description: "Your event has been created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create event. Please try again.",
        variant: "destructive",
      });
      console.error("Create event error:", error);
    },
  });

  const requestAttendanceMutation = useMutation({
    mutationFn: eventService.requestAttendance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Request Sent!",
        description: "Your attendance request has been sent to the event organizer",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to send attendance request",
        variant: "destructive",
      });
      console.error("Request attendance error:", error);
    },
  });

  const updateAttendeeMutation = useMutation({
    mutationFn: ({ attendeeId, status }: { attendeeId: string; status: "approved" | "declined" }) =>
      eventService.updateAttendeeStatus(attendeeId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Attendee Updated",
        description: "Attendee status has been updated",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update attendee status",
        variant: "destructive",
      });
      console.error("Update attendee error:", error);
    },
  });

  const promoteToAdminMutation = useMutation({
    mutationFn: eventService.promoteToAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Promoted to Admin",
        description: "User has been promoted to event admin",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to promote user to admin",
        variant: "destructive",
      });
      console.error("Promote to admin error:", error);
    },
  });

  const removeAttendeeMutation = useMutation({
    mutationFn: eventService.removeAttendee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Attendee Removed",
        description: "Attendee has been removed from the event",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to remove attendee",
        variant: "destructive",
      });
      console.error("Remove attendee error:", error);
    },
  });

  const handleCreateEvent = async (eventData: EventInsert) => {
    await createEventMutation.mutateAsync(eventData);
  };

  const handleRequestAttendance = (eventId: string) => {
    requestAttendanceMutation.mutate(eventId);
  };

  const handleApproveAttendee = (attendeeId: string) => {
    updateAttendeeMutation.mutate({ attendeeId, status: "approved" });
  };

  const handleDeclineAttendee = (attendeeId: string) => {
    updateAttendeeMutation.mutate({ attendeeId, status: "declined" });
  };

  const handlePromoteToAdmin = (attendeeId: string) => {
    promoteToAdminMutation.mutate(attendeeId);
  };

  const handleRemoveAttendee = (attendeeId: string) => {
    removeAttendeeMutation.mutate(attendeeId);
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: "Refreshing Events",
      description: "Loading latest events...",
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600 mb-4">Error loading events</p>
          <Button onClick={handleRefresh}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <RightSideMenu />
      <AdPanel />
      
      <div className="p-4 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-4 mb-2">
              <h1 className="text-2xl font-bold text-gray-800">Private Events</h1>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
            <p className="text-gray-600">Discover and create exclusive events</p>
            {events.length > 0 && (
              <p className="text-sm text-gray-500 mt-2">
                {events.length} event{events.length !== 1 ? 's' : ''} found
              </p>
            )}
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
              <TabsTrigger value="grid" className="flex items-center gap-2">
                <Grid className="w-4 h-4" />
                List View
              </TabsTrigger>
              <TabsTrigger value="map" className="flex items-center gap-2">
                <Map className="w-4 h-4" />
                Map View
              </TabsTrigger>
            </TabsList>

            <TabsContent value="grid">
              <div className="space-y-6">
                {events.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onRequestAttendance={handleRequestAttendance}
                    onViewDetails={setSelectedEvent}
                    isRequesting={requestAttendanceMutation.isPending}
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="map">
              <div className="bg-white rounded-lg p-8 text-center">
                <Map className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Map View Coming Soon</h3>
                <p className="text-gray-600">
                  Interactive map view for events will be available soon
                </p>
              </div>
            </TabsContent>
          </Tabs>

          {events.length === 0 && (
            <div className="bg-white rounded-lg p-8 text-center">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Events Yet</h3>
              <p className="text-gray-600 mb-4">
                Be the first to create a private event
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                Create First Event
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Floating Create Button */}
      <Button
        onClick={() => setIsCreateDialogOpen(true)}
        className="fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 shadow-lg"
        size="icon"
      >
        <Plus className="w-6 h-6" />
      </Button>

      {/* Create Event Dialog */}
      <CreateEventDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreateEvent={handleCreateEvent}
        isCreating={createEventMutation.isPending}
      />

      {/* Event Details Dialog */}
      <EventDetailsDialog
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onApproveAttendee={handleApproveAttendee}
        onDeclineAttendee={handleDeclineAttendee}
        onPromoteToAdmin={handlePromoteToAdmin}
        onRemoveAttendee={handleRemoveAttendee}
        isUpdating={updateAttendeeMutation.isPending || promoteToAdminMutation.isPending || removeAttendeeMutation.isPending}
      />
    </div>
  );
};

export default Events;
